/**
 * A COLUNA "DOCUMENTOS" da lista de faturas (parte C, prévia v3 aprovada pelo Heitor em 06/10/2026).
 *
 * Diz se a nota, o boleto e o e-mail de cada fatura estão como deveriam. A célula fica QUIETA quando está tudo
 * certo e fala, em palavras, só na exceção. Antes, a coluna era só de e-mail, e duas ausências opostas saíam
 * iguais: o cliente que não recebe e-mail e a fatura que deveria ter ido e não foi.
 *
 * ⛔ UMA EXCEÇÃO POR CÉLULA. Com mais de uma, a célula mostra só a mais grave e um "+N"; as outras ficam no
 * `title`. Medido na tabela real: 65 px em dia, 67 com uma exceção, 89 com duas empilhadas, 111 com três; com
 * "+N", 67 px, qualquer que seja o número. Ordem: perigo > aviso > neutro; no empate, nota > boleto > e-mail.
 *
 * ⚠️ "SEM NOTA ATIVA", e não "nunca emitida": o status de nota da fatura é um AGREGADO, e quando todas as notas
 * são canceladas ele volta a vazio. Pela lista não dá para separar "nunca emitida" de "emitida e cancelada".
 *
 * ⚠️ PARCELA DE GRUPO: a nota mora no GRUPO (`invoiceGroup.nfseStatus`), não na parcela. O grupo vem por
 * PARÂMETRO porque o detalhe da fatura não traz `invoiceGroup`; sem o grupo em mãos, a regra não chuta "sem
 * nota ativa" para uma parcela que pode muito bem ter nota.
 */

export type TomDoDocumento = 'quieto' | 'apagado' | 'neutro' | 'aviso' | 'perigo';
export type Documento = 'nota' | 'boleto' | 'cdr' | 'email';

export interface SinalDoDocumento {
  documento: Documento;
  tom: TomDoDocumento;
  texto: string;
  /** O que vai no texto ao passar o mouse, quando diz mais que `texto` (ex.: minutos e valor do CDR). */
  detalhe?: string;
}

export interface FaturaComDocumentos {
  status?: string;
  paymentMethod?: string | null;
  nossoNumero?: string | null;
  boletoStatus?: string | null;
  nfseStatus?: string | null;
  nfcomStatus?: string | null;
  invoiceGroupId?: number | null;
}

/** O resumo do e-mail como a tela já calcula (`getEmailSummary`): vale o último envio de cada destinatário. */
export interface ResumoDoEmail {
  state: string;
  ok: number;
  total: number;
}

export interface ContextoDoEmail {
  /** null = nenhum log de envio. */
  resumo: ResumoDoEmail | null;
  /** O cliente é conhecido na tela? Sem ele, não há como saber se deveria ter ido. */
  clienteConhecido: boolean;
  /** `emailDeliveryType` do cliente; 'nenhum' = "Nao enviar". */
  modelo?: string | null;
  temEmailFinanceiro: boolean;
  /** O log de e-mail desta fatura foi LIDO? false = não carregou: sem resumo, isso é "não sei", não "não enviado".
   *  Ausente conta como lido (quem chama sem saber continua como antes). */
  logConhecido?: boolean;
}

const ABERTA_OU_PARCIAL = ['Aberta', 'Parcial'];

const PESO: Record<TomDoDocumento, number> = { perigo: 3, aviso: 2, neutro: 1, apagado: 0, quieto: 0 };
const ORDEM: Record<Documento, number> = { nota: 0, boleto: 1, cdr: 2, email: 3 };

/** É exceção (fala) ou é o estado em dia (quieto/apagado)? */
export function ehExcecao(s: SinalDoDocumento): boolean {
  return PESO[s.tom] > 0;
}

function sinalDeUmaNota(status: string): SinalDoDocumento | null {
  if (status === 'Autorizada') return null;
  if (status === 'Processando' || status === 'Rascunho') return { documento: 'nota', tom: 'neutro', texto: 'nota em processamento' };
  if (status === 'Erro' || status === 'Rejeitada') return { documento: 'nota', tom: 'perigo', texto: 'nota com erro' };
  // Valor que o código não conhece: cru e neutro, nunca verde.
  return { documento: 'nota', tom: 'neutro', texto: `nota ${status}` };
}

/**
 * A nota. Parcela de grupo lê a do grupo; `grupo` ausente numa parcela = não se sabe, e não se diz nada.
 */
export function sinalDaNota(inv: FaturaComDocumentos, grupo?: { nfseStatus?: string | null } | null): SinalDoDocumento | null {
  const deGrupo = !!inv.invoiceGroupId;
  if (deGrupo && !grupo) return null;
  const presentes = [inv.nfseStatus, inv.nfcomStatus, deGrupo ? grupo?.nfseStatus : null]
    .map((s) => (s == null ? '' : String(s).trim()))
    .filter(Boolean);
  if (presentes.length === 0) {
    if (inv.status === 'Rascunho' || inv.status === 'Cancelada') return null;
    return { documento: 'nota', tom: 'aviso', texto: 'sem nota ativa' };
  }
  return maisGrave(presentes.map(sinalDeUmaNota).filter((s): s is SinalDoDocumento => !!s));
}

export function sinalDoBoleto(inv: FaturaComDocumentos): SinalDoDocumento | null {
  if (inv.paymentMethod !== 'boleto') return null;
  const st = inv.boletoStatus ? String(inv.boletoStatus) : '';
  const aberta = ABERTA_OU_PARCIAL.includes(String(inv.status));
  if (st === 'rejeitado') return { documento: 'boleto', tom: 'perigo', texto: 'boleto rejeitado' };
  if (st === 'baixado') return aberta ? { documento: 'boleto', tom: 'aviso', texto: 'boleto baixado' } : null;
  if (['registrado', 'confirmado', 'pago'].includes(st)) return null;
  if (!inv.nossoNumero) return aberta ? { documento: 'boleto', tom: 'aviso', texto: 'boleto não emitido' } : null;
  if (st) return { documento: 'boleto', tom: 'neutro', texto: `boleto ${st}` };
  return null;
}

const EMAIL_EM_DIA: Record<string, string> = { sent: 'e-mail enviado', delivered: 'e-mail entregue', opened: 'e-mail aberto' };

/**
 * O e-mail. Diferente da nota e do boleto, o estado em dia TAMBÉM aparece (quieto), porque é por ele que se
 * chega ao comprovante. A regra do "e-mail não enviado" é a do cartão "Precisa de você": só fatura Aberta.
 */
export function sinalDoEmail(inv: FaturaComDocumentos, ctx: ContextoDoEmail): SinalDoDocumento | null {
  const r = ctx.resumo;
  if (r) {
    if (r.state === 'bounced') return { documento: 'email', tom: 'perigo', texto: 'e-mail falhou' };
    if (r.state === 'partial') return { documento: 'email', tom: 'aviso', texto: `e-mail parcial ${r.ok}/${r.total}` };
    if (EMAIL_EM_DIA[r.state]) return { documento: 'email', tom: 'quieto', texto: EMAIL_EM_DIA[r.state] };
    return { documento: 'email', tom: 'neutro', texto: `e-mail ${r.state}` };
  }
  if (!ctx.clienteConhecido) return null;
  if ((ctx.modelo || 'nf_fatura_boleto') === 'nenhum') return { documento: 'email', tom: 'apagado', texto: 'não envia' };
  if (inv.status !== 'Aberta') return null;
  if (ctx.logConhecido === false && ctx.temEmailFinanceiro) return { documento: 'email', tom: 'neutro', texto: 'e-mail: não sei' };
  return ctx.temEmailFinanceiro
    ? { documento: 'email', tom: 'aviso', texto: 'e-mail não enviado' }
    : { documento: 'email', tom: 'aviso', texto: 'sem e-mail financeiro' };
}

function maisGrave(sinais: SinalDoDocumento[]): SinalDoDocumento | null {
  return ordenarPorGravidade(sinais)[0] ?? null;
}

export function ordenarPorGravidade(sinais: SinalDoDocumento[]): SinalDoDocumento[] {
  return [...sinais].sort((a, b) => PESO[b.tom] - PESO[a.tom] || ORDEM[a.documento] - ORDEM[b.documento]);
}

/**
 * A célula em DUAS linhas (auditoria do painel, 07/10/2026, decisão do Heitor).
 *
 * Até ali a célula tinha a legenda do modelo de envio do cadastro ("NF+Fat+Boleto") e UMA linha com a
 * exceção mais grave entre nota, boleto e e-mail. O Heitor pediu o contrário: o modelo do cadastro sai, e o
 * e-mail fica sempre à vista, porque é por ele que se chega à trilha. Com a regra antiga, uma nota com erro
 * escondia o e-mail que falhou atrás do "+N", e com ele o caminho para a trilha.
 *
 * - Linha 1, `email`: o estado do e-mail, qualquer que seja o tom. Nunca é escondido por outro documento.
 * - Linha 2, `documento`: a pior exceção entre nota, boleto e CDR, e `mais` = o que ficou de fora.
 *
 * O CDR (decisão do Heitor, 07/10/2026: "com o CDR na coluna documentos") entra NESTA regra, como mais um
 * sinal, e não como terceira linha. Só existe para a fatura cuja assinatura inclui consumo (utils/cdrNaFatura);
 * as outras não recebem nada e a célula delas é a mesma de antes.
 * ⛔ "CDR não aplicado" é a exceção que a linha 2 mostra SEMPRE, mesmo com outra exceção na célula: um aviso
 *    que só existe para o dia em que acontecer não pode nascer escondido atrás de um contador. Consequência
 *    aceita: nesse dia, uma nota com erro na mesma fatura vai para o "+N" (e para o texto ao passar o mouse).
 * - "CDR aplicado" é informação, quieta: ocupa a linha 2 quando ela está livre; com exceção na linha 2, conta
 *   no "+N". Nunca some.
 */
export interface CelulaDeDocumentos {
  email: SinalDoDocumento | null;
  documento: SinalDoDocumento | null;
  /** O que a linha 2 não mostrou: exceções de nota/boleto/CDR e o CDR aplicado (o "+N"). */
  mais: number;
  /** Todas as exceções, e-mail inclusive, da mais grave à menos. */
  excecoes: SinalDoDocumento[];
  /** O CDR da fatura, aplicado ou não; null quando a fatura não precisa de CDR. */
  cdr: SinalDoDocumento | null;
}

/** O estado do CDR como `estadoDoCdr` devolve (utils/cdrNaFatura), ou null quando a fatura não precisa. */
export interface CdrDaCelula {
  aplicado: boolean;
  texto: string;
  titulo?: string;
}

/** A linha 2 e o resto, a partir das exceções de nota/boleto e do CDR. Uma regra para fatura e grupo. */
function segundaLinha(docs: SinalDoDocumento[], cdr: SinalDoDocumento | null) {
  const ordenados = ordenarPorGravidade(docs);
  const cdrAviso = cdr && ehExcecao(cdr) ? cdr : null;
  const documento = cdrAviso ?? ordenados[0] ?? cdr ?? null;
  const fora = [...ordenados, ...(cdr ? [cdr] : [])].filter((s) => s !== documento);
  return { documento, mais: fora.length };
}

export function celulaDeDocumentos(
  inv: FaturaComDocumentos,
  grupo: { nfseStatus?: string | null } | null | undefined,
  email: ContextoDoEmail,
  cdr?: CdrDaCelula | null,
): CelulaDeDocumentos {
  const doEmail = sinalDoEmail(inv, email);
  const doCdr: SinalDoDocumento | null = cdr
    ? { documento: 'cdr', tom: cdr.aplicado ? 'quieto' : 'aviso', texto: cdr.texto, detalhe: cdr.titulo }
    : null;
  const documentos = [sinalDaNota(inv, grupo), sinalDoBoleto(inv)].filter((s): s is SinalDoDocumento => !!s && ehExcecao(s));
  const { documento, mais } = segundaLinha(documentos, doCdr);
  const excecoes = ordenarPorGravidade([
    ...documentos,
    ...(doCdr && ehExcecao(doCdr) ? [doCdr] : []),
    ...(doEmail && ehExcecao(doEmail) ? [doEmail] : []),
  ]);
  return { email: doEmail, documento, mais, excecoes, cdr: doCdr };
}

/**
 * A célula da LINHA DE GRUPO (econômico ou de parcelas): a mesma regra, somada sobre as faturas do grupo.
 *
 * - E-mail: com exceção em alguma fatura, a mais grave e em quantas ("e-mail falhou em 1"); sem exceção,
 *   `emDia` (o texto em dia que a tela já calcula sobre os logs do grupo, ex. "e-mail entregue 2 de 2").
 * - Documento: a mesma `segundaLinha` da fatura, sobre os TIPOS de exceção das faturas ("sem nota ativa em 2");
 *   `mais` = os outros tipos.
 * - CDR, quando só ALGUMAS faturas do grupo precisam: conta só as que precisam; as outras não dizem nada,
 *   como na linha delas. Uma não aplicada → "CDR não aplicado em 1", aviso, e é o que a linha 2 mostra.
 *   Todas as que precisam aplicadas → "CDR aplicado em 2", quieto. Sem "de N": N seria o grupo inteiro,
 *   e daria a entender que as que não precisam estão faltando.
 */
export interface CelulaDoGrupo {
  email: SinalDoDocumento | null;
  documento: SinalDoDocumento | null;
  mais: number;
  excecoes: SinalDoDocumento[];
}

export function celulaDoGrupo(celulas: CelulaDeDocumentos[], emDia: SinalDoDocumento | null): CelulaDoGrupo {
  const mesmo = (a: SinalDoDocumento, b: SinalDoDocumento) => a.documento === b.documento && a.texto === b.texto;
  const emQuantas = (lista: (SinalDoDocumento | null)[], s: SinalDoDocumento) => lista.filter((x) => x && mesmo(x, s)).length;
  const comContagem = (s: SinalDoDocumento, n: number): SinalDoDocumento => ({ ...s, texto: `${s.texto} em ${n}`, detalhe: undefined });

  const emails = celulas.map((c) => c.email);
  const pioresEmail = ordenarPorGravidade(emails.filter((s): s is SinalDoDocumento => !!s && ehExcecao(s)));
  const email = pioresEmail[0] ? comContagem(pioresEmail[0], emQuantas(emails, pioresEmail[0])) : emDia;

  // Exceções de nota/boleto, por TIPO, com em quantas faturas.
  const docs = celulas.flatMap((c) => c.excecoes.filter((s) => s.documento === 'nota' || s.documento === 'boleto'));
  const tipos: SinalDoDocumento[] = [];
  for (const s of ordenarPorGravidade(docs)) if (!tipos.some((t) => mesmo(t, s))) tipos.push(s);
  const tiposComContagem = tipos.map((t) => comContagem(t, emQuantas(docs, t)));

  // CDR: só as faturas que precisam. Uma não aplicada basta para o grupo acusar.
  const cdrs = celulas.map((c) => c.cdr).filter((s): s is SinalDoDocumento => !!s);
  const naoAplicados = cdrs.filter(ehExcecao);
  const cdr = naoAplicados[0]
    ? comContagem(naoAplicados[0], naoAplicados.length)
    : cdrs[0] ? comContagem(cdrs[0], cdrs.length) : null;

  const { documento, mais } = segundaLinha(tiposComContagem, cdr);
  const excecoes = ordenarPorGravidade([
    ...(pioresEmail[0] && email ? [email] : []),
    ...tiposComContagem,
    ...(cdr && ehExcecao(cdr) ? [cdr] : []),
  ]);
  return { email, documento, mais, excecoes };
}
